import React, { useRef, useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { Button } from '@roo/roo'
import Modal from '@roo/roo/Modal'
import UploadExcel from '@/components/UploadExcel'
import LoadingIcon from '@/components/LoadingIcon'
import { failToast, successToast } from '@/common/utils/toast'
import { excelPolling } from '@/common/utils/polling'
import { batchAdjustBalance, fetchBatchAdjustAsyncResult, triggerBatchAdjustFee } from '@/pages/balance/utils/api'
import DownloadLink, { DownloadTemplateTypeEnum } from '@/pages/rule/pages/detail/components/DownloadLink'
import styles from './index.module.less'

interface BatchAdjustModalProps {
  visible: boolean
  rulePackageId: string
  budgetPolicyNo: string
  onClose: () => void
  onSuccess?: () => void
}

const BatchAdjustModal: React.FC<BatchAdjustModalProps> = ({
  visible,
  rulePackageId,
  budgetPolicyNo,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [fileList, setFileList] = useState<{ name: string; fileS3Url?: string }[]>([])
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const excelTimeoutRef = useRef<number>()

  const handleClose = () => {
    clearTimeout(excelTimeoutRef.current)
    setFileList([])
    setUploadStatus('idle')
    setErrorMsg('')
    onClose()
  }

  const handleUpload = async (file: Blob | null) => {
    if (!file) return
    try {
      setUploadStatus('loading')
      setErrorMsg('')
      const token = await batchAdjustBalance({
        byteBuffer: file,
        rulePackageId,
        fileName: (file as File).name,
      })
      excelPolling({
        requestFunction: () => fetchBatchAdjustAsyncResult(token),
        onTimeoutChange: (timeout) => {
          excelTimeoutRef.current = timeout
        },
        onFinish: async (param) => {
          if (param.type === 'success') {
            try {
              await triggerBatchAdjustFee({
                budgetPolicyNo,
                rulePackId: rulePackageId,
                token,
              })
              setUploadStatus('idle')
              setFileList([])
              setErrorMsg('')
              successToast(t('e_pc_batch_adjust_success', '批量调整提交成功'))
              onSuccess?.()
            } catch (triggerError: any) {
              setUploadStatus('error')
              setErrorMsg(triggerError?.message || t('e_pc_batch_adjust_fail', '批量调整失败，请检查文件'))
            }
          } else {
            setUploadStatus('error')
            setErrorMsg(param.data?.message || t('e_pc_batch_adjust_fail', '批量调整失败，请检查文件'))
          }
        },
      })
    } catch (error: any) {
      setUploadStatus('error')
      setErrorMsg(error?.message || t('e_pc_batch_adjust_fail', '批量调整失败，请检查文件'))
    }
  }

  const handleCancelUpload = () => {
    clearTimeout(excelTimeoutRef.current)
    setUploadStatus('idle')
    setFileList([])
    setErrorMsg('')
  }

  return (
    <Modal
      title={t('e_pc_bulk_adjust_balance', '批量调整余额')}
      visible={visible}
      onClose={handleClose}
      footer={
        <div className={styles.modal_footer}>
          <Button onClick={handleClose}>{t('e_pc_cancel', '取消')}</Button>
        </div>
      }
      width={560}
    >
      <div className={styles.modal_content}>
        <UploadExcel
          onUpload={handleUpload}
          fileList={fileList}
          setFileList={setFileList}
          disableDelete={uploadStatus === 'loading'}
        >
          <DownloadLink
            templateType={DownloadTemplateTypeEnum.BALANCE_UPDATE_TEMP}
            rulePackId={rulePackageId}
          >
            {t('staff_download_template', '下载模板')}
          </DownloadLink>
        </UploadExcel>

        {uploadStatus === 'loading' && fileList.length > 0 && (
          <div className={styles.status_row}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <LoadingIcon />
              <span style={{ color: '#666', paddingLeft: 8 }}>{t('e_pc_validation_status', '数据校验中')}</span>
            </div>
            <Button type="text" onClick={handleCancelUpload}>
              <span style={{ color: '#FF5500' }}>{t('e_pc_cancel_action', '取消')}</span>
            </Button>
          </div>
        )}

        {uploadStatus === 'error' && errorMsg && (
          <div className={styles.status_error}>{errorMsg}</div>
        )}
      </div>
    </Modal>
  )
}

export default BatchAdjustModal
